import { Avatar, AvatarImage, AvatarInitials } from "@/components/Avatar";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { Pill } from "@/components/podkit/status/Pill";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading4 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { UserSelector } from "@/components/share/UserSelector";
import type { ShareableResource } from "@/contexts/ShareResourceContext";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import {
    useListGroups,
    useListRoleAssignments,
    useShareResourceWithPrincipal,
    useUnshareResourceWithPrincipal,
} from "@/queries/group-queries";
import { useMembersByIds, type PlainOrganizationMember } from "@/queries/organization-queries";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { formatError } from "@/utils/errors";
import { getUserId } from "@/utils/subjects";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { ResourceRole } from "gitpod-next-api/gitpod/v1/group_pb";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import { Trash2 } from "lucide-react";
import { useCallback, useMemo, type FC } from "react";

// Helper to get role label and description for project roles
const getProjectRoleInfo = (role: ResourceRole): { label: string; description: string } => {
    switch (role) {
        case ResourceRole.PROJECT_ADMIN:
            return {
                label: "Admin",
                description: "Full Project control including sharing and management.",
            };
        case ResourceRole.PROJECT_EDITOR:
            return {
                label: "Editor",
                description: "Can update, delete, and manage Environment classes and Secrets.",
            };
        case ResourceRole.PROJECT_USER:
            return {
                label: "User",
                description: "Basic Project access for viewing and using.",
            };
        default:
            return {
                label: "User",
                description: "Basic Project access for viewing and using.",
            };
    }
};

// Helper to get role label and description for runner roles
const getRunnerRoleInfo = (role: ResourceRole): { label: string; description: string } => {
    switch (role) {
        case ResourceRole.RUNNER_ADMIN:
            return {
                label: "Admin",
                description: "Full Runner control including configuration and management.",
            };
        case ResourceRole.RUNNER_USER:
            return {
                label: "User",
                description: "Can use the runner to create and manage environments.",
            };
        default:
            return {
                label: "User",
                description: "Can use the runner to create and manage environments.",
            };
    }
};

// Helper to get role label and description for workflow roles
const getWorkflowRoleInfo = (role: ResourceRole): { label: string; description: string } => {
    switch (role) {
        case ResourceRole.WORKFLOW_ADMIN:
            return {
                label: "Admin",
                description: "Full automation control including sharing and management.",
            };
        case ResourceRole.WORKFLOW_EXECUTOR:
            return {
                label: "Executor",
                description: "Can run the automation. Executions run with their permissions.",
            };
        case ResourceRole.WORKFLOW_VIEWER:
            return {
                label: "Viewer",
                description: "Can view the automation but not run or modify it.",
            };
        default:
            return {
                label: "Executor",
                description: "Can run the automation. Executions run with their permissions.",
            };
    }
};

// Helper to get available roles for a resource type
const getAvailableRoles = (resourceType: ResourceType): ResourceRole[] => {
    switch (resourceType) {
        case ResourceType.PROJECT:
            return [ResourceRole.PROJECT_USER, ResourceRole.PROJECT_EDITOR, ResourceRole.PROJECT_ADMIN];
        case ResourceType.RUNNER:
            return [ResourceRole.RUNNER_USER, ResourceRole.RUNNER_ADMIN];
        case ResourceType.WORKFLOW:
            return [ResourceRole.WORKFLOW_EXECUTOR];
        default:
            return [];
    }
};

// Helper to get role info based on resource type
const getRoleInfo = (resourceType: ResourceType, role: ResourceRole): { label: string; description: string } => {
    switch (resourceType) {
        case ResourceType.PROJECT:
            return getProjectRoleInfo(role);
        case ResourceType.RUNNER:
            return getRunnerRoleInfo(role);
        case ResourceType.WORKFLOW:
            return getWorkflowRoleInfo(role);
        default:
            return { label: role.toString(), description: "" };
    }
};

type UserWithAccess = {
    user: PlainOrganizationMember;
    role: ResourceRole;
};

type Props = {
    resource: ShareableResource;
    onUsersAdded?: (userIds: string[]) => void;
    onUserRemoved?: (userId: string) => void;
};

export const UsersWithAccessSection: FC<Props> = ({
    resource: { resourceType, resourceId },
    onUsersAdded,
    onUserRemoved,
}) => {
    const { toast } = useToast();
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { data: currentUser } = useAuthenticatedUser();
    const { data: groupsData } = useListGroups();
    const { data: assignmentsData } = useListRoleAssignments({ resourceType, resourceId });
    const shareWithPrincipal = useShareResourceWithPrincipal();
    const unshareWithPrincipal = useUnshareResourceWithPrincipal();

    const availableRoles = useMemo(() => getAvailableRoles(resourceType), [resourceType]);
    const defaultRole = availableRoles[0] || ResourceRole.UNSPECIFIED;

    // Get all direct share groups for this resource with their roles
    const directShareGroupsWithRoles = useMemo(() => {
        if (!groupsData?.groups || !assignmentsData?.assignments) return [];

        const result: { groupId: string; role: ResourceRole }[] = [];

        for (const assignment of assignmentsData.assignments) {
            const group = groupsData.groups.find((g) => g.id === assignment.groupId);
            if (group?.directShare) {
                result.push({
                    groupId: group.id,
                    role: assignment.resourceRole,
                });
            }
        }

        return result;
    }, [groupsData, assignmentsData]);

    // Create a stable key for the query based on group IDs
    const groupIdsKey = useMemo(
        () =>
            directShareGroupsWithRoles
                .map((g) => g.groupId)
                .sort()
                .join(","),
        [directShareGroupsWithRoles],
    );

    // Fetch memberships for all direct share groups in a single query
    const { data: directShareMemberships } = useQuery({
        queryKey: keyWithPrincipal(["directShareMemberships", resourceType, resourceId, groupIdsKey]),
        queryFn: async () => {
            if (directShareGroupsWithRoles.length === 0) return [];

            const results: { userId: string; role: ResourceRole }[] = [];

            // Fetch memberships for each direct share group
            await Promise.all(
                directShareGroupsWithRoles.map(async ({ groupId, role }) => {
                    const response = await api.groupService.listMemberships({
                        groupId,
                        pagination: { pageSize: 100 },
                    });

                    for (const member of response.members) {
                        const userId = getUserId(member.subject);
                        if (userId) {
                            results.push({ userId, role });
                        }
                    }
                }),
            );

            return results;
        },
        enabled: directShareGroupsWithRoles.length > 0,
        staleTime: 30_000,
    });

    // Deduplicate users with their highest role from direct share memberships
    const userRolesMap = useMemo(() => {
        const map = new Map<string, ResourceRole>();
        if (!directShareMemberships) return map;
        for (const { userId, role } of directShareMemberships) {
            const existingRole = map.get(userId);
            if (!existingRole || role > existingRole) {
                map.set(userId, role);
            }
        }
        return map;
    }, [directShareMemberships]);

    // Fetch only the members who have direct access (instead of all org members)
    const accessUserIds = useMemo(() => Array.from(userRolesMap.keys()), [userRolesMap]);
    const { members: accessMembersMap } = useMembersByIds(accessUserIds, { enabled: accessUserIds.length > 0 });

    // Build list of users with direct access
    const usersWithAccess = useMemo(() => {
        const users: UserWithAccess[] = [];
        for (const [userId, role] of userRolesMap) {
            const user = accessMembersMap.get(userId);
            if (user) {
                users.push({ user, role });
            }
        }
        // Sort by name, but put current user first
        return users.sort((a, b) => {
            const aIsCurrentUser = a.user.userId === currentUser?.id;
            const bIsCurrentUser = b.user.userId === currentUser?.id;
            if (aIsCurrentUser && !bIsCurrentUser) return -1;
            if (!aIsCurrentUser && bIsCurrentUser) return 1;
            return a.user.fullName.localeCompare(b.user.fullName);
        });
    }, [userRolesMap, accessMembersMap, currentUser]);

    // IDs to exclude from the user selector: users with access + current user
    const excludeUserIds = useMemo(() => {
        const ids = new Set(usersWithAccess.map((u) => u.user.userId));
        if (currentUser?.id) {
            ids.add(currentUser.id);
        }
        return ids;
    }, [usersWithAccess, currentUser]);

    const invalidateDirectShareMemberships = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: keyWithPrincipal(["directShareMemberships", resourceType, resourceId]),
        });
    }, [queryClient, resourceType, resourceId]);

    const handleAddUsers = useCallback(
        async (userIds: string[]) => {
            try {
                // Share with each user using the default role
                await Promise.all(
                    userIds.map((userId) =>
                        shareWithPrincipal.mutateAsync({
                            resourceType,
                            resourceId,
                            principal: Principal.USER,
                            principalId: userId,
                            role: defaultRole,
                        }),
                    ),
                );

                // Invalidate the direct share memberships query
                invalidateDirectShareMemberships();

                toast({
                    title: "User access granted",
                });

                onUsersAdded?.(userIds);
            } catch (error) {
                toast({
                    title: "Failed to grant user access",
                    description: formatError(error),
                });
            }
        },
        [
            defaultRole,
            resourceType,
            resourceId,
            shareWithPrincipal,
            toast,
            onUsersAdded,
            invalidateDirectShareMemberships,
        ],
    );

    const handleRoleChange = useCallback(
        async (userId: string, value: string) => {
            try {
                // Handle deletion
                if (value === "delete") {
                    await unshareWithPrincipal.mutateAsync({
                        resourceType,
                        resourceId,
                        principal: Principal.USER,
                        principalId: userId,
                    });

                    // Invalidate the direct share memberships query
                    invalidateDirectShareMemberships();

                    toast({
                        title: "User access removed",
                    });

                    onUserRemoved?.(userId);
                    return;
                }

                // Handle role change - unshare first, then share with new role
                const role = parseInt(value) as ResourceRole;

                // First unshare to remove from all direct share groups
                await unshareWithPrincipal.mutateAsync({
                    resourceType,
                    resourceId,
                    principal: Principal.USER,
                    principalId: userId,
                });

                // Then share with the new role
                await shareWithPrincipal.mutateAsync({
                    resourceType,
                    resourceId,
                    principal: Principal.USER,
                    principalId: userId,
                    role,
                });

                // Invalidate the direct share memberships query
                invalidateDirectShareMemberships();

                toast({
                    title: "User access updated",
                });
            } catch (error) {
                toast({
                    title: "Failed to update user access",
                    description: formatError(error),
                });
            }
        },
        [
            resourceType,
            resourceId,
            shareWithPrincipal,
            unshareWithPrincipal,
            toast,
            onUserRemoved,
            invalidateDirectShareMemberships,
        ],
    );

    return (
        <div className="flex flex-col gap-2">
            <Heading4 className="font-medium">Users with access</Heading4>

            <UserSelector excludeUserIds={excludeUserIds} onAdd={handleAddUsers} />

            {usersWithAccess.length === 0 ? (
                <div className="flex items-center justify-center rounded-lg border border-border-light bg-surface-02 px-6 py-8">
                    <Text className="text-sm text-content-secondary">No users have direct access</Text>
                </div>
            ) : (
                <div className="flex flex-col gap-1">
                    {usersWithAccess.map(({ user, role }) => {
                        const isCurrentUser = user.userId === currentUser?.id;
                        return (
                            <div
                                key={user.userId}
                                className="flex items-center gap-2 px-0 py-1"
                                data-testid={`user-assignment-${user.userId}`}
                            >
                                <Avatar size={32} className="size-8 rounded-full">
                                    <AvatarImage src={user.avatarUrl} alt={user.fullName} />
                                    <AvatarInitials name={user.fullName} size={32} />
                                </Avatar>
                                <div className="flex min-w-0 flex-1 flex-col">
                                    <Text className="flex items-center gap-2 truncate text-base">
                                        {user.fullName}
                                        {isCurrentUser && <Pill variant="success">me</Pill>}
                                    </Text>
                                    <Text className="truncate text-sm text-content-tertiary">{user.email}</Text>
                                </div>
                                <Select
                                    value={role.toString()}
                                    onValueChange={(value) => handleRoleChange(user.userId, value)}
                                >
                                    <SelectTrigger
                                        className="w-32"
                                        loading={false}
                                        data-testid={`role-select-${user.userId}`}
                                    >
                                        <SelectValue>
                                            <span className="text-base">{getRoleInfo(resourceType, role).label}</span>
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent className="w-fit" align="end">
                                        {availableRoles.map((r) => {
                                            const roleInfo = getRoleInfo(resourceType, r);
                                            return (
                                                <SelectItem key={r} value={r.toString()}>
                                                    <div className="mr-6 flex flex-col gap-0.5">
                                                        <span className="text-base">{roleInfo.label}</span>
                                                        <span className="truncate text-sm text-content-secondary">
                                                            {roleInfo.description}
                                                        </span>
                                                    </div>
                                                </SelectItem>
                                            );
                                        })}
                                        <SelectSeparator />
                                        <SelectItem value="delete">
                                            <div className="flex items-center gap-2 text-content-destructive">
                                                <Trash2 className="size-4" />
                                                <span className="text-base">Remove access</span>
                                            </div>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
